import { Component, computed, effect, EffectRef, ElementRef, inject, signal, Signal, ViewChild, WritableSignal } from '@angular/core';
import { BenefitsDescriptionsComponent } from './benefits-descriptions/benefits-descriptions.component';
import { BenefitsService } from '@services/benefits.service';
import { GroupAssignedAmenity } from '@models/interfaces/amenities/AmenityGroupAssigned.interface';
import { BenefitsContact, BenefitsDescriptions } from '@models/types/components/Benefits.type';
import { ButtonType } from '@models/enums/components/Button/ButtonType.enum';
import { BenefitsCarouselComponent } from './benefits-carousel/benefits-carousel.component';
import { MenuCardComponent } from '../../menu/menu-card/menu-card.component';
import { BenefitsContactComponent } from './benefits-contact/benefits-contact.component';
import anime from 'animejs';
import { LayoutService } from '@services/layout.service';
import { BottomSheetService } from '@services/components/bottom-sheet.service';

@Component({
  selector: 'app-benefits-container',
  standalone: true,
  imports: [
    MenuCardComponent,
    BenefitsCarouselComponent,
    BenefitsDescriptionsComponent,
    BenefitsContactComponent
    // ButtonComponent
  ],
  templateUrl: './benefits-container.component.html',
  styleUrl: './benefits-container.component.css'
})
export class BenefitsContainerComponent {

  @ViewChild('benefitsContainer', { static: true, read: ElementRef})
  public benefitsContainer!: ElementRef<HTMLDivElement>;

  button = ButtonType.Pill;

  // SERVICES
  public  readonly benefitsService: BenefitsService                       = inject(BenefitsService);
  private readonly bottomSheet:     BottomSheetService                    = inject(BottomSheetService);

  // STATE 
  public  readonly showContainer:  Signal<boolean>                        = this.benefitsService.showContainer;
  private readonly isMobile:       Signal<boolean>                        = inject(LayoutService).isMobile;

  // COMPONENT STATE
  public           hideElement:    boolean                                = true;
  public           amenityKey:     number | null                          = null;
  
  // SIGNALS
  public  readonly amenities:      Signal<GroupAssignedAmenity[]>         = this.benefitsService.amenities; 
  public  readonly amenity:        WritableSignal<GroupAssignedAmenity|undefined> = signal(undefined);
  public  readonly descriptions:   Signal<BenefitsDescriptions|null>              = computed((): BenefitsDescriptions | null => {

    if(!this.amenity()) return null;

    return {
      publicDescription:      this.amenity()?.publicDescription || ''
    }

  })
  public  readonly carouselData:   Signal<{img: string[], text: string[]} | undefined> = computed((): {img: string[], text: string[]} | undefined => {

   return this.amenity()?.assets?.reduce((acc, asset) => {
    if(asset.isFromCarousel){
      acc.img.push(asset.urlSigned);
      acc.text.push(asset.text);
    }
    return acc;
   }, {img: [] as string[], text:[] as string[]}) ?? undefined;

  })

  public readonly  contact:        Signal<BenefitsContact|null> = computed((): BenefitsContact | null => {
    if(!this.amenity()) return null;

    return {
      emailAddress1: this.amenity()?.emailAddress1  || '',
      phoneNumber1:  this.amenity()?.phoneNumber1   || '',
      emailAddress2: this.amenity()?.emailAddress2  || null,
      phoneNumber2:  this.amenity()?.phoneNumber2   || null
    }

  });

  // EFFECTS
  private readonly toggleContainer:   EffectRef = effect(()=>{
    
    if(!this.hideElement && !this.showContainer()) this.hideAmenity();

    this.toggleContainerContent(this.showContainer());
    
  },{allowSignalWrites: true});


  public toggleAmenity(amenityKey: number): void {
    
    // Si seleccionamos la misma, desactivamos
    if(this.amenityKey === amenityKey){
      
      this.amenityKey = null;
      this.amenity.set(undefined);

      // BOTTOM SHEET BEHAVIOR
      if(this.isMobile() && this.bottomSheet.state() === 'full' && this.amenities().length === 1) this.bottomSheet.updateFromComponent('open');

      return; 
    }

    // Si no la seteamos
    this.amenityKey = amenityKey;
    this.amenity.set(this.amenities()[amenityKey] || undefined);    

    // BOTTOM SHEET BEHAVIOR
    if(this.isMobile() && this.bottomSheet.state() === 'open') this.bottomSheet.updateFromComponent('full')
  }

  public hideAmenity(): void{
    this.amenity.set(undefined);
    this.amenityKey = null;
  }

  private toggleContainerContent(show: boolean): void {

    const targets: HTMLElement[] = Array.from(this.benefitsContainer.nativeElement.children).map(el => el.firstChild as HTMLElement);

    if(this.hideElement && show) this.hideElement = false;

    //open benefits when menu item is selected
    if (this.amenities().length) {
      if (!this.isMobile()) {
        this.amenityKey = 0;
        this.amenity.set(this.amenities()[0]);
      }
    }


    anime.timeline({
      begin:    (): void => this.benefitsService.isAnimating.set(true),
      complete: (): void => { 
        if(!this.showContainer()) this.hideElement = true;
        this.benefitsService.isAnimating.set(false);
      }, 
      duration: show ? 300 : 200,
      easing: show ? 'easeInOutQuad' : 'easeInOutCubic',
    })
    .add({
      targets: this.benefitsContainer.nativeElement,
      opacity: show ? 1 : 0,
    })
    .add({
      targets,
      opacity: show ? 1 : 0,
      easing: 'easeInOutQuad',
      delay: this.amenities().length > 1 ?  anime.stagger(100, {start: 0}) : 0,
    }, this.amenities().length > 1  ? '-=50' : '')

  }


}
